import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, InputNumber, Select, Space, Tag, Radio,
  Alert, message, Popconfirm, Image,
} from 'antd';
import request from '../utils/request';

// 积分商城设置：从商品管理数据中选择可积分兑换的药品，配置积分价/兑换方式/过期时间
const PointsMall: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // 默认展示全部商品：需在全部商品中将药品设为可兑换后，才会出现在积分商品 Tab
  const [pointsOnly, setPointsOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/product', {
        params: { page: p, pageSize: 10, pointsOnly: pointsOnly ? 1 : 0 },
      });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pointsOnly]);

  const openModal = (record: any) => {
    form.resetFields();
    form.setFieldsValue({
      pointsPrice: record.pointsPrice ? Number(record.pointsPrice) : undefined,
      redeemType: record.redeemType || 'ONLINE',
      limitPerUser: record.limitPerUser ?? undefined,
    });
    setEditItem(record);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const payload: any = {
        pointsPrice: values.pointsPrice,
        redeemType: values.redeemType,
        pointsExpireDays: null, // 无过期选项：兑换记录永不过期，清空历史残留值
        limitPerUser: values.limitPerUser ?? null,
      };
      // 积分商品无需规格：普通商品转为积分商品时清空规格（与商品管理逻辑一致）
      if (editItem.skus?.length) payload.skus = [];
      await request.put(`/product/${editItem.id}`, payload);
      message.success('保存成功');
      setModalOpen(false);
      setEditItem(null);
      fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  // 取消积分上架：清空积分价与过期时间，恢复普通商品（需回商品管理补规格才能现金售卖）
  const handleOffline = async (record: any) => {
    await request.put(`/product/${record.id}`, { pointsPrice: null, pointsExpireDays: null });
    message.success('已取消积分上架');
    fetchList();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '商品', width: 220,
      render: (_: any, r: any) => (
        <Space>
          {(r.image || r.images) && (
            <Image
              src={r.image || String(r.images).split('\n')[0].trim()}
              width={32} height={32} style={{ objectFit: 'cover' }}
            />
          )}
          <span>{r.name}</span>
        </Space>
      ),
    },
    { title: '分类', dataIndex: 'category', width: 100, render: (c: any) => c?.name || '-' },
    {
      title: '上架状态', dataIndex: 'status', width: 90,
      render: (v: number) => v === 1
        ? <Tag color="green">上架</Tag>
        : <Tag>下架</Tag>,
    },
    {
      title: '现金价', dataIndex: 'price', width: 90,
      render: (v: any) => `¥${Number(v).toFixed(2)}`,
    },
    {
      title: '积分价', dataIndex: 'pointsPrice', width: 90,
      render: (v: any) => v ? <Tag color="purple">{Number(v)} 积分</Tag> : <Tag>未上架</Tag>,
    },
    {
      title: '兑换方式', dataIndex: 'redeemType', width: 110,
      render: (v: string, r: any) => r.pointsPrice
        ? <Tag color={v === 'OFFLINE' ? 'cyan' : 'geekblue'}>{v === 'OFFLINE' ? '线下到店核销' : '线上快递发货'}</Tag>
        : '-',
    },
    {
      title: '每人限兑', dataIndex: 'limitPerUser', width: 90,
      render: (v: any, r: any) => !r.pointsPrice ? '-' : (v ? `${v} 件` : '不限'),
    },
    {
      title: '操作', width: 160, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <Space>
          <Button size="small" type="primary" onClick={() => openModal(r)}>
            {r.pointsPrice ? '编辑设置' : '设为积分商品'}
          </Button>
          {r.pointsPrice ? (
            <Popconfirm
              title="确认取消积分上架？"
              description="取消后该商品退出积分商城；如需现金售卖请回商品管理补规格"
              onConfirm={() => handleOffline(r)}
            >
              <Button size="small" danger>取消上架</Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>积分商城设置</h2>
        <Radio.Group
          value={pointsOnly}
          onChange={(e) => { setPointsOnly(e.target.value); setPage(1); }}
          options={[
            { label: '积分商品', value: true },
            { label: '全部商品', value: false },
          ]}
          optionType="button"
          buttonStyle="solid"
        />
      </div>
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="从商品管理的药品中选择可积分兑换的商品：填写积分价即上架到小程序积分商城（不再出现在现金商城）；线下兑换到店出示核销码，线上兑换快递发货。"
      />
      <Table
        columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1200 }}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }}
      />

      <Modal
        title={`积分设置 - ${editItem?.name || ''}`}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditItem(null); }}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="保存"
        destroyOnClose
      >
        {editItem?.skus?.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message="该商品当前为普通售卖商品（已配置规格），设为积分商品后规格将被清空，且不在现金商城售卖。"
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            name="pointsPrice" label="兑换所需积分"
            rules={[{ required: true, message: '请输入兑换所需积分' }]}
            tooltip="用户兑换该商品消耗的积分数"
          >
            <InputNumber min={1} precision={0} placeholder="如 100" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="redeemType" label="兑换方式"
            rules={[{ required: true, message: '请选择兑换方式' }]}
            tooltip="线上=快递发货到收货地址；线下=到店出示核销码核销"
          >
            <Select
              options={[
                { label: '线上（快递发货）', value: 'ONLINE' },
                { label: '线下（到店核销）', value: 'OFFLINE' },
              ]}
            />
          </Form.Item>
          <Form.Item name="limitPerUser" label="每人限兑（件）" tooltip="留空表示不限购；已过期的兑换不占名额">
            <InputNumber min={1} precision={0} placeholder="不限" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PointsMall;
